import { Menu, Popover, Transition } from "@headlessui/react";
import { BellIcon, MenuIcon, XIcon } from "@heroicons/react/outline";
import { SearchIcon } from "@heroicons/react/solid";
import { Fragment, KeyboardEvent, useState } from "react";
import { FaSignOutAlt, FaUserCircle } from "react-icons/fa";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import stringToColor from "string-to-color";
import { useAuth } from "../hooks/useAuth";
import GlobalSettingsDialog from "./globalSettings/GlobalSettingsDialog";
import { KTRLogo } from "./shared/ktr";

const navigation = [
  { name: "Dashboard", href: "/" },
  { name: "Users", href: "/users" },
  { name: "Teams", href: "/teams" },
  { name: "Events", href: "/events" },
  { name: "Features", href: "/features" },
  { name: "Templates", href: "/templates" },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

interface CustomNavLinkProps {
  name: string;
  url: string;
  // icon: React.ReactNode;
}

export function CustomNavLink({ name, url }: CustomNavLinkProps) {
  return (
    <NavLink
      to={url}
      className={({ isActive }) =>
        `inline-flex items-center px-1 pt-1 border-b-4  font-medium ${
          isActive
            ? "border-red-500 text-gray-900"
            : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
        }`
      }
    >
      {name}
    </NavLink>
  );
}

interface CustomNavLinkCollapsedProps {
  name: string;
  url: string;
  close: () => void;
}

export function CustomNavLinkCollapsed({
  name,
  url,
  close,
}: CustomNavLinkCollapsedProps) {
  return (
    <NavLink
      to={url}
      onClick={() => close()}
      className={({ isActive }) =>
        `block rounded-md px-3 py-2 text-base text-gray-900 font-medium hover:bg-gray-100 hover:text-gray-800 ${
          isActive ? "" : ""
        }`
      }
    >
      {name}
    </NavLink>
  );
}

export default function Navbar() {
  // const location = useLocation();
  let navigate = useNavigate();
  const { logout, user } = useAuth();
  // const { activeProject } = useGlobalState();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [term, setTerm] = useState("");

  async function triggerLogout() {
    logout();
    navigate("/login");
    toast.info("Logged out successfully");
  }

  function handleEnter(event: KeyboardEvent) {
    if (event.key === "Enter") {
      navigate(`?term=${term}`);
    }
  }

  return (
    <header className="bg-white shadow">
      <div
        className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8"
        style={{ maxWidth: "100rem" }}
      >
        <Popover className="flex justify-between h-16">
          <div className="flex px-2 lg:px-0">
            <div className="flex-shrink-0 flex items-center">
              <div className="h-10 w-10">
                <KTRLogo />
              </div>
              <span className="ml-2 font-bold text-2xl font-mono">admin</span>
            </div>
            <nav
              aria-label="Global"
              className="hidden lg:ml-6 lg:flex lg:space-x-8"
            >
              {navigation.map((item) => (
                <CustomNavLink name={item.name} url={item.href} />
              ))}
              {/* {activeProject && (
                <StudioNavLink
                  name="Active Project"
                  url={`/studio/${activeProject?.id}`}
                  activeProject={activeProject}
                />
              )} */}
            </nav>
          </div>
          {/* {location.pathname.indexOf("studio") === -1 && !loading && (
            <div className="mr-20 w-1/2">
              <NavbarPlayer
                currentTrack={currentTrack}
                playbackState={playbackState}
                togglePlaybackState={togglePlaybackState}
              />
            </div>
          )} */}
          <div className="flex-1 flex items-center justify-center px-2 lg:ml-6 lg:justify-end">
            <div className="max-w-lg w-full lg:max-w-xs">
              <label htmlFor="search" className="sr-only">
                Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  id="search"
                  name="search"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white shadow-sm placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-600 focus:border-blue-600 sm:text-sm"
                  placeholder="Search"
                  type="search"
                  onChange={(search) => setTerm(search.target.value)}
                  onKeyDown={handleEnter}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center lg:hidden">
            {/* Mobile menu button */}
            <Popover.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
              <span className="sr-only">Open main menu</span>
              <MenuIcon className="block h-6 w-6" aria-hidden="true" />
            </Popover.Button>
          </div>
          <Transition.Root as={Fragment}>
            <div className="lg:hidden">
              <Transition.Child
                as={Fragment}
                enter="duration-150 ease-out"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="duration-150 ease-in"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Popover.Overlay
                  className="z-20 fixed inset-0 bg-black bg-opacity-25"
                  aria-hidden="true"
                />
              </Transition.Child>

              <Transition.Child
                as={Fragment}
                enter="duration-150 ease-out"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="duration-150 ease-in"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Popover.Panel
                  focus
                  className="z-30 absolute top-0 right-0 max-w-none w-full p-2 transition transform origin-top"
                >
                  {({ close }) => (
                    <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white divide-y divide-gray-200">
                      <div className="pt-3 pb-2">
                        <div className="flex items-center justify-between px-4">
                          <div className="flex items-center">
                            <div className="h-10 w-10">
                              <KTRLogo />
                            </div>
                            <span className="ml-2 font-bold text-2xl font-mono">
                              admin
                            </span>
                          </div>
                          <div className="-mr-2">
                            <Popover.Button className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                              <span className="sr-only">Close menu</span>
                              <XIcon className="h-6 w-6" aria-hidden="true" />
                            </Popover.Button>
                          </div>
                        </div>
                        <div className="mt-3 px-2 space-y-1">
                          {navigation.map((item) => (
                            <CustomNavLinkCollapsed
                              name={item.name}
                              url={item.href}
                              close={close}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="pt-4 pb-2">
                        <div className="flex items-center px-5">
                          <div className="flex-shrink-0">
                            <FaUserCircle
                              className="w-8 h-8 bg-white rounded-full"
                              color={stringToColor(user?.id)}
                            />
                          </div>
                          <div className="ml-3">
                            <div className="text-base font-medium text-gray-800">
                              {user?.firstname}
                            </div>
                            <div className="text-sm font-medium text-gray-500">
                              {user?.email}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="ml-auto flex-shrink-0 bg-white p-1 text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <span className="sr-only">View notifications</span>
                            <BellIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                        <div className="mt-3 px-2 space-y-1">
                          {/* {userNavigation.map((item) => ( */}
                          {/* <div
                            className="block rounded-md px-3 py-2 text-base text-gray-900 font-medium hover:bg-gray-100 hover:text-gray-800"
                            onClick={() => {
                              close();
                              setSettingsOpen(!settingsOpen);
                            }}
                          >
                            Settings
                          </div> */}
                          <div
                            className="block rounded-md px-3 py-2 text-base text-gray-900 font-medium hover:bg-gray-100 hover:text-gray-800"
                            onClick={() => {
                              close();
                              triggerLogout();
                            }}
                          >
                            Sign out
                          </div>
                          {/* ))} */}
                        </div>
                      </div>
                    </div>
                  )}
                </Popover.Panel>
              </Transition.Child>
            </div>
          </Transition.Root>
          <div className="hidden lg:ml-4 lg:flex lg:items-center">
            <button
              type="button"
              className="flex-shrink-0 bg-white p-1 text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <span className="sr-only">View notifications</span>
              <BellIcon className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Profile dropdown */}
            <Menu as="div" className="ml-4 relative flex-shrink-0">
              <div>
                <Menu.Button className="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <span className="sr-only">Open user menu</span>
                  {/* <img
                    className="h-8 w-8 rounded-full"
                    src={user.}
                    alt=""
                  /> */}
                  <FaUserCircle
                    className="w-8 h-8 bg-white rounded-full"
                    color={stringToColor(user?.id)}
                  />
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {/* <Menu.Item>
                    {({ active }) => (
                      <div
                        onClick={() => setSettingsOpen(!settingsOpen)}
                        className={classNames(
                          active ? "bg-gray-100" : "",
                          "flex px-4 py-2 text-sm text-gray-700 items-center"
                        )}
                      >
                        <FaCogs className="mr-2" />
                        Settings
                      </div>
                    )}
                  </Menu.Item> */}
                  <Menu.Item>
                    {({ active }) => (
                      <div
                        onClick={() => triggerLogout()}
                        className={classNames(
                          active ? "bg-gray-100" : "",
                          "flex px-4 py-2 text-sm text-gray-700 items-center"
                        )}
                      >
                        <FaSignOutAlt className="mr-2" />
                        Log out
                      </div>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </Popover>
      </div>
      <GlobalSettingsDialog isOpen={settingsOpen} setIsOpen={setSettingsOpen} />
    </header>
  );
}
